import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { CheckCircle, SpinnerGap, Warning } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [amount, setAmount] = useState(0);
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const provider = params.get("provider");
    let stop = false;

    // PayPal returns with ?provider=paypal&token=<orderId>&PayerID=...
    if (provider === "paypal") {
      const orderId = params.get("token");
      if (!orderId) { setStatus("error"); return; }
      (async () => {
        try {
          const { data } = await api.post(`/payments/paypal/capture/${orderId}`);
          setAmount(data.amount_cents || 0);
          if (data.payment_status === "paid") setStatus("paid"); else setStatus("timeout");
        } catch { setStatus("timeout"); }
      })();
      return;
    }

    // Stripe returns with ?session_id=...
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAmount(data.amount_cents || 0);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (tries.current >= 8) { if (!stop) setStatus("timeout"); return; }
      if (!stop) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, search]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#08111F] p-6 text-white">
      <div data-testid="payment-success" className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">
        {status === "checking" && (
          <>
            <SpinnerGap size={48} className="mx-auto animate-spin text-primary" />
            <h1 className="mt-5 font-display text-2xl font-semibold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-white/60">This takes a few seconds. Please don't close this page.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-400" />
            <h1 className="mt-5 font-display text-2xl font-semibold">Wallet funded</h1>
            <p className="mt-2 text-sm text-white/60">{money(amount)} has been added to your Universal Wallet as paid balance.</p>
            <button data-testid="go-wallet" onClick={() => navigate("/app/wallet")}
              className="mt-6 w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">Go to Wallet</button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Warning size={52} weight="fill" className="mx-auto text-amber-300" />
            <h1 className="mt-5 font-display text-2xl font-semibold">Still processing</h1>
            <p className="mt-2 text-sm text-white/60">Your payment may take a moment to confirm. Your balance will update automatically once it clears.</p>
            <Link to="/app/wallet" className="mt-6 inline-block w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground">Back to Wallet</Link>
          </>
        )}
      </div>
    </div>
  );
}
